import { IOrderLocation, ISiteConfig } from 'Order/types/location';
import {
  isValidCoords,
  isValidEmail,
  isValidNoteField,
  isValidPhoneField,
  isValidTextField,
  isValidVLANIdField,
} from './validation';
import { requireCoords } from 'shared/utils/addresses/helperFunctions';
import { AddressType, CablingType, IQuote } from 'Quotes/types/store';
import { isFTTXQuote } from 'Quotes/utils/isFTTXQuote';
import { ProductType } from 'Quotes/types/productTypes';
import { isSupplierNNAT } from 'Quotes/utils/isPriceNNAT';
import { Supplier } from 'Quotes/types/supplier';

function getConfigIsComplete(quote: IQuote, siteConfig: ISiteConfig, cablingType?: CablingType | null, isCampusCrossConnect?: boolean, access_type?: string | null) {
  const fttxQuote = isFTTXQuote(quote);

  if(quote.productType === ProductType.OpticalP2P && cablingType !== undefined) {
    let validateFloorRoomRack = cablingType === CablingType.Fibre ? "yes" : "no";
    if (access_type === Supplier.OPENREACH) validateFloorRoomRack = "yes";
    
    if(!isCampusCrossConnect && validateFloorRoomRack === "no") {
      if(!siteConfig.connectorType) return false;
      if(!siteConfig.mediaType) return false;
      return true;
    }
  }

  return (
    (fttxQuote ? true : siteConfig.connectorType) &&
    (fttxQuote ? true : siteConfig.mediaType) &&
    (fttxQuote ? true : siteConfig.powerType) &&
    (!siteConfig.vlanTagging ? true : isValidVLANIdField(siteConfig.vlanId)) &&
    isValidTextField(siteConfig.floor) &&
    isValidTextField(siteConfig.room) &&
    isValidTextField(siteConfig.rackId)
  );
}

export function isCompleted(
  {
    locationData: { fullAddress, addressType, fullAddressNotListed, pafAddressNotListed },
    endCompanyName,
    siteReadiness,
    siteConfig,
    secondarySiteConfig,
    siteContact,
    siteCoords,
  }: IOrderLocation,
  quote: IQuote,
  access_type: string | null,
  cablingType?: CablingType | null,
  isCampusCrossConnect?: boolean,
  hasDataCentreReference?: boolean
): boolean {
  const openreachSwiftIsComplete = isFTTXQuote(quote) || siteReadiness.openreachSwift !== undefined;
  const readinessIsComplete =
    openreachSwiftIsComplete &&
    ((siteReadiness.hazardsOnSite &&
      isValidTextField(siteReadiness.hazardsOnSiteDescription) &&
      isValidNoteField(siteReadiness.hazardsOnSiteDescription || '')) ||
      !siteReadiness.hazardsOnSite);

  if (!readinessIsComplete) {
    return false;
  }

  const configIsComplete = getConfigIsComplete(quote, siteConfig, cablingType, isCampusCrossConnect, access_type);
  const secondarySiteConfigIsComplete = secondarySiteConfig ? getConfigIsComplete(quote, secondarySiteConfig, cablingType, isCampusCrossConnect, access_type) : true;

  if (!configIsComplete || !secondarySiteConfigIsComplete) {
    return false;
  }

  const siteIsComplete =
    isValidTextField(siteContact.firstName) &&
    isValidTextField(siteContact.surname) &&
    isValidPhoneField(siteContact.phone) &&
    isValidEmail(siteContact.email) &&
    isValidNoteField(siteContact.notes);

  if (!siteIsComplete) {
    return false;
  }

  if (!isValidTextField(endCompanyName)) {
    return false;
  }

  const isOpticalDataCentreFlow = quote.productType === ProductType.OpticalP2P && hasDataCentreReference;
  const isManualAddress =
    addressType === AddressType.MANUAL || fullAddressNotListed || pafAddressNotListed;
  const requiresCoordinates = isManualAddress || requireCoords(fullAddress, addressType);

  if (
    !isOpticalDataCentreFlow &&
    requiresCoordinates &&
    !isValidCoords({
      lon: siteCoords.longitude,
      lat: siteCoords.latitude,
      easting: siteCoords.easting,
      northing: siteCoords.northing,
    })
  ) {
    return false;
  }

  const eastingsNorhingsIsComplete =
    siteCoords.easting || siteCoords.northing
      ? isValidTextField(siteCoords.easting) && isValidTextField(siteCoords.northing)
      : true;
  const latLonIsComplete =
    siteCoords.latitude || siteCoords.longitude
      ? isValidTextField(siteCoords.latitude) && isValidTextField(siteCoords.longitude)
      : true;

  if (!eastingsNorhingsIsComplete || !latLonIsComplete) {
    return false;
  }

  if (
    isOpticalDataCentreFlow ||
    !requiresCoordinates ||
    !isManualAddress ||
    (addressType === AddressType.PAF && isSupplierNNAT(access_type))
  ) {
    return true;
  }

  return (
    (isValidTextField(fullAddress.building_name) || isValidTextField(fullAddress.building_number)) &&
    isValidTextField(fullAddress.county) &&
    isValidTextField(fullAddress.post_town) &&
    isValidTextField(fullAddress.postcode) &&
    isValidTextField(fullAddress.street)
  );
}
